
import React, { useState, useEffect, useRef } from 'react';
import { VanityAPI } from '../services/api';
import Cropper from 'react-easy-crop';

const TestImagePage = () => {
    // ... (Existing State)
    const [images, setImages] = useState<{ id: string, name: string, image_path: string, created_at: string, signedUrl?: string }[]>([]);
    const [standardImages, setStandardImages] = useState<any[]>([]); // New list

    // ... (Existing State: name, file, uploading, debugError, editingId)
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [debugError, setDebugError] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // CROP STATE
    const [cropFile, setCropFile] = useState<string | null>(null);
    const [cropRawFile, setCropRawFile] = useState<File | null>(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [aspect, setAspect] = useState(4 / 3); // Default product aspect
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [standardEntityName, setStandardEntityName] = useState(''); // New name input

    useEffect(() => {
        loadImages();
        loadStandardEntities();
    }, []);

    const loadImages = async () => {
        try {
            const imgs = await VanityAPI.getTestImages();
            setImages(imgs);
            setDebugError(null);
        } catch (err: any) {
            setDebugError(`Load Error: ${err.message}`);
        }
    };

    const loadStandardEntities = async () => {
        try {
            const std = await VanityAPI.getTestEntities();
            setStandardImages(std);
        } catch (err: any) {
            console.error("Standard load error", err);
        }
    };

    // ... (Existing handleUpload for legacy test)

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name) return;

        setUploading(true);
        setDebugError(null);
        try {
            if (editingId) {
                // EDIT MODE
                await VanityAPI.updateTestImage(editingId, name, file || undefined);
            } else {
                // CREATE MODE
                if (!file) { alert('File required for new uploads'); setUploading(false); return; }
                await VanityAPI.uploadTestImage(name, file);
            }

            setName('');
            setFile(null);
            setEditingId(null);
            await loadImages();
            if (fileInputRef.current) fileInputRef.current.value = '';
        } catch (err: any) {
            console.error(err);
            setDebugError(`Action Failed: ${err.message || JSON.stringify(err)}\n\nCheck console for full details.`);
        } finally {
            setUploading(false);
        }
    };

    // STANDARD UPLOAD HANDLER
    const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const f = e.target.files[0];
            setCropRawFile(f);
            setCropFile(URL.createObjectURL(f));
        }
    };

    const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
        setCroppedAreaPixels(croppedAreaPixels);
    };

    const handleStandardUpload = async () => {
        if (!cropRawFile) return;
        if (!standardEntityName.trim()) { alert('Please enter a name for the entity'); return; }

        setUploading(true);
        setDebugError(null);

        try {
            // Prepare normalized crop JSON
            // react-easy-crop gives pixels and percentages.
            // We want normalized (0-1) relative to original image dimensions if possible, 
            // but the backend sharp process might need different format or we trust the resize pipeline.
            // The "Golden Standard" document requested:
            // { unit: "normalized", x: 0.12, y: 0.08, w: 0.76, h: 0.76 ... }

            // NOTE: react-easy-crop 'croppedArea' is in percentages (0-100).
            // We can convert 0-100 to 0-1.

            // Use a ref to capture the latest croppedArea if needed, or stick to pixels?
            // Wait, onCropComplete gives us `croppedArea` (percent) and `croppedAreaPixels` (px).
            // Let's store `croppedArea` (percent) in state too if we want to be precise, 
            // but let's assume we use standard Sharp resize for now.
            // Wait, the backend DOES NOT APPLY CROP currently in code (Step 946), it just stores json.
            // AND the resize logic currently is just `fit: inside`. 
            // To truly support crop, we need to add `.extract()` to sharp pipeline. 
            // BUT for this step, user asked to "Implement in text image - for one last test (with crop tool)".
            // I will send the crop JSON and let the backend store it. 
            // The user's guide says "Server converts normalized crop box to pixel crop on the original dimensions."

            // Let's define the JSON payload nicely.
            const cropJson = {
                unit: 'px', // Or normalized
                x: croppedAreaPixels.x,
                y: croppedAreaPixels.y,
                width: croppedAreaPixels.width,
                height: croppedAreaPixels.height
            };

            // 1. Upload Image (Get Image ID)
            const imgRes = await VanityAPI.uploadStandardImage(
                cropRawFile,
                'test_entity',
                '00000000-0000-0000-0000-000000000000', // Temp ID, or maybe we don't link here yet?
                'gallery',
                cropJson
            );

            // 2. Create Entity Link
            await VanityAPI.linkTestEntity(standardEntityName, imgRes.id);

            setCropFile(null);
            setCropRawFile(null);
            setStandardEntityName('');
            await loadStandardEntities();

        } catch (err: any) {
            setDebugError(`Standard Upload Failed: ${err.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleCancelCrop = () => {
        setCropFile(null);
        setCropRawFile(null);
    }

    // ... (startEdit, etc.)
    const startEdit = (img: any) => {
        setEditingId(img.id);
        setName(img.name);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };


    return (
        <div className="p-8 max-w-6xl mx-auto pb-32">
            <h1 className="text-3xl font-light mb-8 font-serif">Railway Image Architecture Test</h1>

            {debugError && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
                    <strong className="font-bold">Error!</strong>
                    <span className="block sm:inline ml-2 whitespace-pre-wrap">{debugError}</span>
                </div>
            )}

            {/* --- SECTION 3: STANDARD (CROP + LINK) --- */}
            <div className={`bg-gray-50 p-6 rounded shadow mb-12 border-2 ${cropFile ? 'border-vanity-pink' : 'border-dashed border-gray-300'}`}>
                <h2 className="text-xl mb-4 font-serif flex items-center gap-2">
                    <span className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
                    Standard Upload (With Crop & Entity Link)
                </h2>

                {!cropFile ? (
                    <div className="text-center py-8">
                        <input
                            type="file"
                            id="std-upload"
                            className="hidden"
                            onChange={onSelectFile}
                            accept="image/*"
                        />
                        <label htmlFor="std-upload" className="cursor-pointer bg-black text-white px-6 py-3 rounded hover:opacity-80 transition-opacity">
                            Select Image to Crop & Upload
                        </label>
                        <p className="text-xs text-gray-400 mt-2">Will be processed into Thumb, Middle, Full variants.</p>
                    </div>
                ) : (
                    <div className="animate-fadeIn">
                        <div className="mb-4">
                            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1">Entity Name (Test Table)</label>
                            <input
                                type="text"
                                value={standardEntityName}
                                onChange={e => setStandardEntityName(e.target.value)}
                                className="w-full border p-2 rounded"
                                placeholder="e.g. New Summer Campaign"
                            />
                        </div>

                        <div className="relative h-[400px] bg-black rounded overflow-hidden mb-4">
                            <Cropper
                                image={cropFile}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspect}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        </div>
                        <div className="flex gap-4 items-center mb-4">
                            <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Aspect Ratio:</div>
                            <button onClick={() => setAspect(4 / 3)} className={`px-3 py-1 text-xs rounded border ${aspect === 4 / 3 ? 'bg-black text-white' : 'bg-white'}`}>4:3 (Product)</button>
                            <button onClick={() => setAspect(3 / 4)} className={`px-3 py-1 text-xs rounded border ${aspect === 3 / 4 ? 'bg-black text-white' : 'bg-white'}`}>3:4 (Portrait)</button>
                            <button onClick={() => setAspect(1 / 1)} className={`px-3 py-1 text-xs rounded border ${aspect === 1 / 1 ? 'bg-black text-white' : 'bg-white'}`}>1:1 (Square)</button>
                            <button onClick={() => setAspect(16 / 9)} className={`px-3 py-1 text-xs rounded border ${aspect === 16 / 9 ? 'bg-black text-white' : 'bg-white'}`}>16:9 (Hero)</button>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={handleStandardUpload}
                                disabled={uploading}
                                className="bg-vanity-pink text-white px-6 py-2 rounded font-bold hover:brightness-110 disabled:opacity-50"
                            >
                                {uploading ? 'Processing...' : 'Confirm Crop & Link'}
                            </button>
                            <button onClick={handleCancelCrop} className="text-gray-500 hover:text-black px-4 py-2">
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* --- LIST TEST ENTITIES (LINKED) --- */}
            {standardImages.length > 0 && (
                <div className="mb-12">
                    <h2 className="text-xl mb-6 font-serif">Test Entities (Linked to 'images')</h2>
                    <div className="grid grid-cols-1 gap-8">
                        {standardImages.map((img: any) => (
                            <div key={img.entity_id} className="border p-4 rounded bg-white flex flex-col md:flex-row gap-6">
                                {/* Thumb */}
                                <div className="md:w-32 shrink-0">
                                    <div className="text-xs font-mono text-gray-500 mb-1">Thumb (300px)</div>
                                    <img src={img.url_thumb || img.url_original} className="w-full h-auto border" />
                                </div>
                                {/* Middle */}
                                <div className="md:w-64 shrink-0">
                                    <div className="text-xs font-mono text-gray-500 mb-1">Middle (800px)</div>
                                    <img src={img.url_middle || img.url_original} className="w-full h-auto border" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-lg font-bold mb-1">{img.entity_name}</h3>
                                    <div className="text-xs text-gray-400 mb-2 font-mono">{img.entity_id}</div>

                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 bg-gray-50 p-3 rounded">
                                        <div><span className="font-bold">Original:</span> {img.original_width}x{img.original_height} ({img.original_format})</div>
                                        <div><span className="font-bold">Key:</span> {img.key_original}</div>
                                        <div className="col-span-2 overflow-hidden font-mono truncate border-t pt-2 mt-2">
                                            <span className="font-bold">Crop JSON:</span> {JSON.stringify(img.crop_json)}
                                        </div>
                                    </div>
                                    <a href={img.url_original} target="_blank" className="inline-block mt-4 text-xs bg-gray-200 px-3 py-1 rounded hover:bg-gray-300">Open Original</a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}


            {/* ... Legacy Section ... */}
            <div className="bg-white p-6 rounded shadow mb-12 border-l-4 border-gray-400 opacity-60">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-serif">{editingId ? 'Edit Asset' : '1. Upload (Private Bucket)'}</h2>
                    {editingId && <button onClick={() => { setEditingId(null); setName(''); setFile(null); }} className="text-xs uppercase font-bold text-gray-400 hover:text-black">Cancel Edit</button>}
                </div>
                {/* ... existing form ... */}
                <form onSubmit={handleUpload} className="space-y-4">
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">Image Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            className="w-full border p-2 rounded"
                            placeholder="e.g. Summer Campaign"
                        />
                    </div>
                    <div>
                        <label className="block text-sm text-gray-600 mb-1">File {editingId ? '(Optional - leave empty to keep existing)' : ''}</label>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={e => setFile(e.target.files?.[0] || null)}
                            className="w-full"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={uploading || (!editingId && !file) || !name}
                        className={`px-6 py-2 rounded text-white disabled:opacity-50 transition-all ${editingId ? 'bg-vanity-pink' : 'bg-black'}`}
                    >
                        {uploading ? (editingId ? 'Updating...' : 'Uploading...') : (editingId ? 'Update & Save' : 'Upload & Sign')}
                    </button>
                </form>
            </div>

            {/* ... Verify legacy ... */}
            <div>
                <h2 className="text-xl mb-6 font-serif">2. Verify (Legacy Signed URLs)</h2>
                {/* ... existing grid ... */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    {images.map((img) => (
                        <div key={img.id} className="border p-4 rounded bg-white">
                            {/* ... image card ... */}
                            <div className="aspect-[3/4] bg-gray-100 mb-2 relative overflow-hidden group">
                                <img
                                    src={img.signedUrl}
                                    alt={img.name}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                            <div className="flex justify-between items-start mt-2">
                                <div><div className="text-sm font-medium">{img.name}</div></div>
                                <button onClick={() => startEdit(img)} className="text-[10px] font-black uppercase tracking-widest text-vanity-pink hover:bg-vanity-pink hover:text-white px-2 py-1 rounded border border-vanity-pink transition-all">Edit</button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

        </div>
    );
};

export default TestImagePage;
